import { ReactElement, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSearchAnalytics } from '@client/analytics/context/search/SearchAnalyticsContext';
import { NoResults } from '@client/catalog/components/NoResults';
import {
  useCatalogFacetFiltersData,
  useCatalogPaginationFilters,
  useFetchCatalogSearchAction,
  useSearchData,
} from '@client/catalog/hooks';
import { FILTERS, getIsInkFinderAvailable } from '@client/catalog/utils/facets';
import {
  getCatalogSearchQuery,
  getCategoryFilter,
  getFiltersValuesFromSearchParams,
  getFilterValueFromSearchParams, prepareFilters,
} from '@client/catalog/utils/filters';
import { calculateCatalogQueryHash } from '@client/catalog/utils/queryHash';
import { RemoteRecordError } from '@client/common/components/RemoteRecordHandler';
import { useActiveLanguage } from '@client/i18n/hooks';
import { getPathnameWithSearch } from '@client/routes/utils/url';
import { Catalog } from './Catalog';
import { Props } from './CatalogContainer.types';

export const CatalogContainer = ({
  getScrollPosition,
  hiddenSections = [],
  ...originalProps
}: Props): ReactElement => {
  const history = useHistory();
  const location = useLocation();
  const { search } = location;
  const activeLanguage = useActiveLanguage();
  const catalog = useSearchData();
  const { paginationFilters } = useCatalogPaginationFilters();
  const {
    availableFacetFilters,
    activeFacetFilters,
    activeFacetFiltersCount,
    facetFiltersWithDefaults,
  } = useCatalogFacetFiltersData(hiddenSections);
  const fetchCatalogSearch = useFetchCatalogSearchAction();
  const categoryFilter = getCategoryFilter(search);
  const facetFilters = getFiltersValuesFromSearchParams(FILTERS.facetFilters, search);
  const searchFilter = getFilterValueFromSearchParams(FILTERS.query, search) as string;
  const catalogFilters = catalog.data?.filters || [];
  const isInkFinderAvailable = getIsInkFinderAvailable(catalogFilters);
  const searchQuery = getCatalogSearchQuery(
    categoryFilter,
    prepareFilters(facetFiltersWithDefaults),
    paginationFilters,
    searchFilter,
  );
  const queryIdentifier = calculateCatalogQueryHash(
    categoryFilter,
    facetFilters,
    paginationFilters,
    searchFilter,
    activeLanguage,
  );
  const { analyzeSearchFilterClick } = useSearchAnalytics();
  const isQueryLoaded = catalog.identifier === queryIdentifier && catalog.isLoaded;

  useEffect((): void => {
    if (catalog.isLoaded && history.action === 'POP') {
      const scrollPosition = getScrollPosition(
        getPathnameWithSearch(location),
      );
      window.scrollTo(0, scrollPosition);
    }
  }, [catalog.isLoaded, history.action, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryFilter]);

  useEffect((): void => {
    if (!isQueryLoaded) {
      fetchCatalogSearch({ query: searchQuery, identifier: queryIdentifier });
    }
  }, [searchQuery, queryIdentifier, isQueryLoaded]);

  if (!catalog || (!catalog.isLoading && !catalog.data.productsCount)) {
    return <NoResults />;
  }

  return (
    <RemoteRecordError
      remoteRecords={[catalog]}
      shouldOpenNotFoundPage
    >
      <Catalog
        {...originalProps}
        catalog={catalog}
        availableFacetFilters={availableFacetFilters}
        activeFacetFilters={activeFacetFilters}
        isInkFinderAvailable={isInkFinderAvailable}
        activeFacetFiltersCount={activeFacetFiltersCount}
        onFilterChange={analyzeSearchFilterClick}
      />
    </RemoteRecordError>
  );
};
